import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import message from 'src/components/Message';
import {
  acsIntegrationClient,
  PdkHolderDetailRow,
  PdkHolderImportResult,
  PdkHolderImportRow,
} from 'src/apis/acsIntegrationClient';
import {buildingServiceFind, apartmentServiceFind, roomServiceFind} from 'src/apis';
import {useAppSelector} from 'src/redux/hooks';

type Props = {
  open: boolean;
  integrationId: string | null;
  onClose: () => void;
  onCompleted?: () => void;
};

type EditableRow = PdkHolderImportRow & {
  selected: boolean;
  alreadyInEntrio: boolean;
  alreadyImported: boolean;
  /** Unit PDK has this holder in, once step 2 has expanded the row. */
  pdkUnitName?: string;
};

type BuildingLite = {id: string; name: string; apartmentId?: string};
type ApartmentLite = {id: string; name: string; buildingId?: string};

const STEPS = ['Review holders', 'Fill in details', 'Confirm & import'];

const requiredKeys: (keyof PdkHolderImportRow)[] = [
  'firstName',
  'lastName',
  'email',
  'unitNumber',
  'apartmentName',
  'buildingName',
];

function rowMissingFields(r: EditableRow): string[] {
  return requiredKeys.filter(k => !r[k] || String(r[k]).trim() === '');
}

export function ImportPdkHoldersWizard({open, integrationId, onClose, onCompleted}: Props) {
  const account = useAppSelector(s => s.account);
  const orgId = account?.userDetail?.organizations?.[0]?.id as string | undefined;

  const [step, setStep] = useState(0);
  const [loadingHolders, setLoadingHolders] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailFailures, setDetailFailures] = useState<
    {externalHolderId: string; message: string}[]
  >([]);
  const [skippedDuplicates, setSkippedDuplicates] = useState<string[]>([]);
  const [holders, setHolders] = useState<EditableRow[]>([]);
  const [buildings, setBuildings] = useState<BuildingLite[]>([]);
  const [apartments, setApartments] = useState<ApartmentLite[]>([]);
  // Cache of rooms per building name (for Unit suggestions). Populated lazily.
  const [roomsByBuilding, setRoomsByBuilding] = useState<Record<string, string[]>>({});
  const [loadingRoomsFor, setLoadingRoomsFor] = useState<Set<string>>(new Set());
  const [bulkBuilding, setBulkBuilding] = useState('');
  const [bulkApartment, setBulkApartment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<PdkHolderImportResult | null>(null);

  const loadHolders = useCallback(async () => {
    if (!integrationId) return;
    setLoadingHolders(true);
    try {
      const res = await acsIntegrationClient.listPdkHolders(integrationId);
      const rows = (res.data.data || []).map<EditableRow>(h => ({
        externalHolderId: h.externalHolderId,
        firstName: h.firstName || '',
        lastName: h.lastName || '',
        email: h.email || '',
        phoneNumber: '',
        unitNumber: '',
        apartmentName: '',
        buildingName: '',
        floorNumber: '',
        alreadyInEntrio: h.alreadyInEntrio,
        alreadyImported: h.alreadyImported,
        // Default-select holders not yet in Entrio.
        selected: !h.alreadyInEntrio,
      }));
      setHolders(rows);
    } catch (e: unknown) {
      const err = e as {response?: {data?: {message?: string}}};
      message.error(err?.response?.data?.message || 'Failed to fetch PDK holders');
    } finally {
      setLoadingHolders(false);
    }
  }, [integrationId]);

  const loadOrgRefs = useCallback(async () => {
    if (!orgId) return;
    try {
      const [bRes, aRes] = await Promise.all([
        buildingServiceFind(orgId, undefined, undefined),
        apartmentServiceFind(orgId, undefined, undefined, undefined),
      ]);
      const bsRaw =
        ((bRes as unknown as {data?: {buildings?: Array<BuildingLite & {apartmentId?: string; apartment_id?: string}>}})?.data?.buildings) || [];
      const apsRaw = (aRes as unknown as {data?: {apartments?: ApartmentLite[]}})?.data?.apartments;
      const aps =
        Array.isArray(apsRaw)
          ? apsRaw
          : Array.isArray(((aRes as unknown) as {data?: ApartmentLite[]})?.data)
            ? ((aRes as unknown) as {data: ApartmentLite[]}).data
            : [];
      setBuildings(
        bsRaw.map(b => ({
          id: String(b.id),
          name: b.name,
          apartmentId: b.apartmentId ? String(b.apartmentId) : (b.apartment_id ? String(b.apartment_id) : undefined),
        }))
      );
      setApartments(
        aps.map(a => ({
          id: String(a.id),
          name: a.name,
          buildingId: (a as unknown as {building_id?: string; buildingId?: string}).building_id ||
            (a as {buildingId?: string}).buildingId,
        }))
      );
    } catch (e) {
      console.warn('Failed to load buildings/apartments', e);
    }
  }, [orgId]);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setResult(null);
    setBulkBuilding('');
    setBulkApartment('');
    setRoomsByBuilding({});
    setLoadingRoomsFor(new Set());
    setDetailFailures([]);
    setSkippedDuplicates([]);
    void loadHolders();
    void loadOrgRefs();
  }, [open, loadHolders, loadOrgRefs]);

  /**
   * Lazily fetch room/unit names for a given building (matched by name) so the
   * Unit Autocomplete can suggest existing units. Building names that don't
   * match any existing building (i.e. the user typed a brand-new name) just
   * cache an empty list — the user can still type a unit number freely, and
   * the backend's findOrCreateRoom will create it during import.
   */
  const fetchRoomsForBuilding = useCallback(
    async (buildingName: string) => {
      if (!buildingName) return;
      if (buildingName in roomsByBuilding) return;
      if (loadingRoomsFor.has(buildingName)) return;
      const b = buildings.find(x => x.name === buildingName);
      if (!b) {
        // Unknown building — cache empty so we don't keep retrying.
        setRoomsByBuilding(prev => ({...prev, [buildingName]: []}));
        return;
      }
      setLoadingRoomsFor(prev => new Set(prev).add(buildingName));
      try {
        const res = await roomServiceFind(b.id, undefined, 1000, 1);
        const data = (res as unknown as {data?: {rooms?: Array<{name?: string}>}}).data;
        const names = Array.isArray(data?.rooms)
          ? data!.rooms!.map(r => r?.name).filter((n): n is string => Boolean(n))
          : [];
        setRoomsByBuilding(prev => ({...prev, [buildingName]: names}));
      } catch (e) {
        console.warn(`Failed to load rooms for building ${buildingName}`, e);
        setRoomsByBuilding(prev => ({...prev, [buildingName]: []}));
      } finally {
        setLoadingRoomsFor(prev => {
          const next = new Set(prev);
          next.delete(buildingName);
          return next;
        });
      }
    },
    [buildings, roomsByBuilding, loadingRoomsFor]
  );

  const selectedRows = useMemo(() => holders.filter(h => h.selected), [holders]);
  const selectedWithErrors = useMemo(
    () => selectedRows.filter(r => rowMissingFields(r).length > 0),
    [selectedRows]
  );
  const canGoStep2 = step === 0 && selectedRows.length > 0;
  const canImport = step === 1 && selectedRows.length > 0 && selectedWithErrors.length === 0;

  const updateRow = (extId: string, patch: Partial<EditableRow>) => {
    setHolders(prev => prev.map(h => (h.externalHolderId === extId ? {...h, ...patch} : h)));
  };

  const applyBulkBuilding = () => {
    const name = bulkBuilding.trim();
    if (!name) return;
    setHolders(prev =>
      prev.map(h =>
        h.selected ? {...h, buildingName: name} : h
      )
    );
    void fetchRoomsForBuilding(name);
  };

  /**
   * Leaving the review step, expand only the selected holders. PDK's holder
   * list carries neither the unit nor (for some holders) the email, and both
   * cost one request per holder, so we pay for the rows the admin picked
   * instead of the whole system.
   *
   * A holder that turns out to already exist in Entrio — possible when the
   * list hid its email — is deselected rather than imported as a duplicate.
   */
  const expandSelectedAndContinue = async () => {
    if (!integrationId) return;
    const ids = selectedRows.map(r => r.externalHolderId);
    if (ids.length === 0) return;
    setLoadingDetails(true);
    try {
      const res = await acsIntegrationClient.getPdkHolderDetails(integrationId, ids);
      const details = res.data.data;
      const byId = new Map(details.holders.map(d => [d.externalHolderId, d]));
      const duplicates = selectedRows
        .map(r => byId.get(r.externalHolderId))
        .filter((d): d is PdkHolderDetailRow => Boolean(d?.alreadyInEntrio));

      setHolders(prev =>
        prev.map(row => {
          const d = byId.get(row.externalHolderId);
          if (!d) return row;
          const email = row.email || d.email;
          if (d.alreadyInEntrio) {
            return {...row, email, alreadyInEntrio: true, selected: false};
          }
          return {
            ...row,
            email,
            alreadyImported: d.alreadyImported,
            pdkUnitName: d.unitName,
            // Don't overwrite a unit the admin already typed before going Back.
            unitNumber: row.unitNumber || d.unitName,
          };
        })
      );
      setDetailFailures(details.failures);
      setSkippedDuplicates(
        duplicates.map(
          d => `${d.firstName} ${d.lastName}`.trim() || d.email || d.externalHolderId
        )
      );

      if (duplicates.length === ids.length) {
        message.warning(
          'Every selected holder already has an Entrio user. Nothing left to import.'
        );
        return;
      }
      setStep(1);
    } catch (e: unknown) {
      const err = e as {response?: {data?: {message?: string}}};
      message.error(err?.response?.data?.message || 'Failed to fetch holder details from PDK');
    } finally {
      setLoadingDetails(false);
    }
  };

  const applyBulkApartment = () => {
    if (!bulkApartment) return;
    const a = apartments.find(x => x.name === bulkApartment);
    if (!a) return;
    const linkedBuilding = a.buildingId ? buildings.find(b => b.id === a.buildingId) : undefined;
    setHolders(prev =>
      prev.map(h =>
        h.selected
          ? {
              ...h,
              apartmentName: a.name,
              ...(linkedBuilding ? {buildingName: linkedBuilding.name} : {}),
            }
          : h
      )
    );
  };

  const handleImport = async () => {
    if (!integrationId) return;
    setSubmitting(true);
    try {
      const payload: PdkHolderImportRow[] = selectedRows.map(r => ({
        externalHolderId: r.externalHolderId,
        firstName: r.firstName.trim(),
        lastName: r.lastName.trim(),
        email: r.email.trim(),
        phoneNumber: r.phoneNumber?.trim() || undefined,
        unitNumber: r.unitNumber.trim(),
        apartmentName: r.apartmentName.trim(),
        buildingName: r.buildingName.trim(),
        floorNumber: r.floorNumber?.trim() || undefined,
      }));
      const res = await acsIntegrationClient.importPdkHolders(integrationId, payload);
      setResult(res.data.data);
      const ok = res.data.data?.success?.length || 0;
      const fail = res.data.data?.errors?.length || 0;
      if (fail === 0) {
        message.success(`Imported ${ok} holder(s) from PDK`);
      } else if (ok === 0) {
        message.error(`Import failed (${fail} error${fail > 1 ? 's' : ''})`);
      } else {
        message.warning(`Imported ${ok}, ${fail} error${fail > 1 ? 's' : ''}`);
      }
      onCompleted?.();
    } catch (e: unknown) {
      const err = e as {response?: {data?: {message?: string; details?: unknown}}};
      message.error(err?.response?.data?.message || 'Import failed');
    } finally {
      setSubmitting(false);
    }
  };

  const apartmentsScopedToBuildingId = (buildingId: string | undefined) => {
    if (!buildingId) return apartments;
    return apartments.filter(a => !a.buildingId || a.buildingId === buildingId);
  };

  /**
   * When an apartment is selected, restrict building suggestions to buildings
   * that belong to that apartment (entrio_buildings.apartment_id === apt.id).
   * If the apartment is unknown, or has no buildings yet, fall back to all
   * buildings — the user can still type a new name and the backend will
   * auto-create the building under that apartment on import.
   */
  const buildingsScopedToApartmentName = (apartmentName: string): BuildingLite[] => {
    const t = apartmentName.trim();
    if (!t) return buildings;
    const apt = apartments.find(a => a.name === t);
    if (!apt) return buildings;
    // Defensive: if the API returned no apartmentId for any building (legacy
    // data, missing field), we can't filter — show all so the user isn't
    // stuck. Otherwise filter strictly.
    const haveApartmentField = buildings.some(b => b.apartmentId);
    if (!haveApartmentField) return buildings;
    return buildings.filter(b => b.apartmentId === apt.id);
  };

  const apartmentOptionsFor = (h: EditableRow) => {
    if (!h.buildingName) return apartments;
    const building = buildings.find(b => b.name === h.buildingName);
    if (!building) return apartments;
    return apartmentsScopedToBuildingId(building.id);
  };

  /** Keeps chosen apartment in MenuItem list when building filter hides it — MUI Select can otherwise coerce value to \"\" and wipe state */
  const apartmentSelectOptionsFor = (h: EditableRow) => {
    const filtered = apartmentOptionsFor(h);
    const chosen = (h.apartmentName || '').trim();
    if (!chosen) return filtered;
    if (filtered.some(a => a.name === chosen)) return filtered;
    const extra = apartments.find(a => a.name === chosen);
    return extra ? [extra, ...filtered] : filtered;
  };

  const renderStep0 = () => (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        These are the holders currently in your PDK system. Holders already linked to an Entrio
        user (matched by email) are grayed out.
      </Typography>
      {loadingHolders ? (
        <Stack alignItems="center" sx={{py: 4}}>
          <CircularProgress size={24} />
        </Stack>
      ) : (
        <TableContainer sx={{maxHeight: 480}}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedRows.length > 0 && selectedRows.length < holders.filter(h => !h.alreadyInEntrio).length
                    }
                    checked={
                      holders.length > 0 &&
                      selectedRows.length === holders.filter(h => !h.alreadyInEntrio).length
                    }
                    onChange={e => {
                      const v = e.target.checked;
                      setHolders(prev =>
                        prev.map(h => (h.alreadyInEntrio ? h : {...h, selected: v}))
                      );
                    }}
                  />
                </TableCell>
                <TableCell>First name</TableCell>
                <TableCell>Last name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {holders.map(h => (
                <TableRow
                  key={h.externalHolderId}
                  sx={{
                    opacity: h.alreadyInEntrio ? 0.5 : 1,
                    bgcolor: h.alreadyInEntrio ? 'action.hover' : undefined,
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      disabled={h.alreadyInEntrio}
                      checked={h.selected}
                      onChange={e => updateRow(h.externalHolderId, {selected: e.target.checked})}
                    />
                  </TableCell>
                  <TableCell>{h.firstName || <em style={{color: '#999'}}>(missing)</em>}</TableCell>
                  <TableCell>{h.lastName || <em style={{color: '#999'}}>(missing)</em>}</TableCell>
                  <TableCell>{h.email || <em style={{color: '#999'}}>(missing)</em>}</TableCell>
                  <TableCell>
                    {h.alreadyInEntrio ? (
                      <Chip size="small" label="Already in Entrio" />
                    ) : h.alreadyImported ? (
                      <Chip size="small" label="Re-import" color="warning" />
                    ) : (
                      <Chip size="small" label="New" color="success" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="body2" color="text.secondary">
        {selectedRows.length} selected of {holders.length} total
      </Typography>
      <Typography variant="caption" color="text.secondary">
        PDK&apos;s holder list doesn&apos;t include units, and hides the email of some holders.
        Both are fetched for the holders you select when you continue.
      </Typography>
      {loadingDetails && <LinearProgress />}
    </Stack>
  );

  const renderStep1 = () => (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Fill in apartment, building, and unit for each selected holder. Required fields are marked
        in red. The import won&apos;t proceed until all selected rows are complete.
      </Typography>
      <Alert severity="info" sx={{py: 0.5}}>
        Type a new <strong>Building</strong> or <strong>Unit</strong> name to create it on import.
        <strong> Apartments</strong> must already exist in your org — only existing apartments can
        be selected. Units already assigned in PDK are pre-filled and can be changed.
      </Alert>

      {skippedDuplicates.length > 0 && (
        <Alert severity="warning">
          Deselected {skippedDuplicates.length} holder
          {skippedDuplicates.length > 1 ? 's' : ''} that already have an Entrio user:{' '}
          {skippedDuplicates.join(', ')}. PDK&apos;s holder list had hidden their email.
        </Alert>
      )}

      {detailFailures.length > 0 && (
        <Alert severity="warning">
          Couldn&apos;t load PDK details for {detailFailures.length} holder
          {detailFailures.length > 1 ? 's' : ''}; their unit wasn&apos;t pre-filled. Enter it
          manually or go back and retry.
        </Alert>
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{flexWrap: 'wrap'}}>
        <TextField
          select
          size="small"
          label="Bulk-set Apartment"
          value={bulkApartment}
          onChange={e => setBulkApartment(e.target.value)}
          sx={{minWidth: 200}}
        >
          <MenuItem value="">—</MenuItem>
          {apartments.map(a => (
            <MenuItem key={a.id} value={a.name}>
              {a.name}
            </MenuItem>
          ))}
        </TextField>
        <Button
          size="small"
          variant="outlined"
          onClick={applyBulkApartment}
          disabled={!bulkApartment}
        >
          Apply to selected
        </Button>
        <Divider orientation="vertical" flexItem />
        <Autocomplete
          size="small"
          freeSolo
          options={buildingsScopedToApartmentName(bulkApartment).map(b => b.name)}
          value={bulkBuilding}
          onInputChange={(_, v) => setBulkBuilding(v || '')}
          sx={{minWidth: 220}}
          renderInput={params => (
            <TextField
              {...params}
              label="Bulk-set Building"
              placeholder="Existing or new"
            />
          )}
        />
        <Button size="small" variant="outlined" onClick={applyBulkBuilding} disabled={!bulkBuilding}>
          Apply to selected
        </Button>
      </Stack>

      <TableContainer sx={{maxHeight: 480}}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name / Email</TableCell>
              <TableCell>Apartment *</TableCell>
              <TableCell>Building *</TableCell>
              <TableCell>Unit *</TableCell>
              <TableCell>Floor</TableCell>
              <TableCell>Phone</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedRows.map(h => {
              const missing = rowMissingFields(h);
              const apartmentOpts = apartmentSelectOptionsFor(h);
              return (
                <TableRow key={h.externalHolderId}>
                  <TableCell sx={{minWidth: 220}}>
                    <Typography variant="body2" fontWeight={600}>
                      {h.firstName} {h.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {h.email}
                    </Typography>
                    {missing.length > 0 && (
                      <Tooltip title={`Missing: ${missing.join(', ')}`}>
                        <Chip
                          size="small"
                          color="error"
                          label={`${missing.length} missing`}
                          sx={{ml: 1}}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{minWidth: 180}}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={h.apartmentName}
                      error={!h.apartmentName}
                      onChange={e => {
                        const name = e.target.value;
                        const apt = apartments.find(a => a.name === name);
                        const patch: Partial<EditableRow> = {apartmentName: name};
                        if (name && apt) {
                          // If the row's current building doesn't belong to
                          // the new apartment, clear it so the user picks one
                          // from the filtered list (or types a new one).
                          const currentBuilding = buildings.find(
                            b => b.name === h.buildingName
                          );
                          if (
                            currentBuilding &&
                            currentBuilding.apartmentId &&
                            currentBuilding.apartmentId !== apt.id
                          ) {
                            patch.buildingName = '';
                            patch.unitNumber = '';
                          }
                        }
                        updateRow(h.externalHolderId, patch);
                      }}
                    >
                      <MenuItem value="">—</MenuItem>
                      {apartmentOpts.map(a => (
                        <MenuItem key={a.id} value={a.name}>
                          {a.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{minWidth: 200}}>
                    <Autocomplete
                      size="small"
                      freeSolo
                      options={buildingsScopedToApartmentName(h.apartmentName).map(b => b.name)}
                      value={h.buildingName}
                      onInputChange={(_, v) => {
                        updateRow(h.externalHolderId, {
                          buildingName: v || '',
                          // Reset unit suggestions as building changed; keep typed unit value
                        });
                        if (v) void fetchRoomsForBuilding(v);
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={!h.buildingName}
                          placeholder="Existing or new"
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell sx={{minWidth: 160}}>
                    <Autocomplete
                      size="small"
                      freeSolo
                      options={
                        h.buildingName && roomsByBuilding[h.buildingName]
                          ? roomsByBuilding[h.buildingName]
                          : []
                      }
                      loading={loadingRoomsFor.has(h.buildingName)}
                      value={h.unitNumber}
                      onInputChange={(_, v) =>
                        updateRow(h.externalHolderId, {unitNumber: v || ''})
                      }
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={!h.unitNumber}
                          placeholder="Existing or new"
                        />
                      )}
                    />
                    {h.pdkUnitName && (
                      <Typography variant="caption" color="text.secondary">
                        {h.unitNumber === h.pdkUnitName
                          ? 'From PDK'
                          : `PDK has ${h.pdkUnitName}`}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{minWidth: 100}}>
                    <TextField
                      size="small"
                      fullWidth
                      value={h.floorNumber || ''}
                      onChange={e => updateRow(h.externalHolderId, {floorNumber: e.target.value})}
                    />
                  </TableCell>
                  <TableCell sx={{minWidth: 140}}>
                    <TextField
                      size="small"
                      fullWidth
                      value={h.phoneNumber || ''}
                      onChange={e => updateRow(h.externalHolderId, {phoneNumber: e.target.value})}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedWithErrors.length > 0 && (
        <Alert severity="warning">
          {selectedWithErrors.length} row{selectedWithErrors.length > 1 ? 's' : ''} still need
          required fields. Fill them in to enable Import.
        </Alert>
      )}
    </Stack>
  );

  const renderStep2 = () => (
    <Stack spacing={2}>
      {!result ? (
        <>
          <Typography variant="body2">
            Ready to import <strong>{selectedRows.length}</strong> holder
            {selectedRows.length === 1 ? '' : 's'} from PDK into Entrio. Each will be created as a
            tenant linked to its building, apartment, and unit.
          </Typography>
          <Alert severity="info">
            This uses the same logic as the CSV bulk import — emails must be unique and rooms must
            have capacity. Errors per row will be shown here after import.
          </Alert>
          {submitting && <LinearProgress />}
        </>
      ) : (
        <>
          <Alert severity={result.errors.length === 0 ? 'success' : 'warning'}>
            {result.success.length} imported, {result.errors.length} failed.
          </Alert>
          {result.errors.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Errors
              </Typography>
              <TableContainer sx={{maxHeight: 280}}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((e, i) => (
                      <TableRow key={i}>
                        <TableCell>{e.row}</TableCell>
                        <TableCell>{e.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{pr: 6}}>
        Import people from PDK
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={submitting}
          sx={{position: 'absolute', right: 8, top: 8}}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} sx={{mb: 3}}>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {step === 0 && renderStep0()}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
      </DialogContent>
      <DialogActions>
        {step > 0 && !result && (
          <Button onClick={() => setStep(s => s - 1)} disabled={submitting}>
            Back
          </Button>
        )}
        {step === 0 && (
          <Button
            variant="contained"
            disabled={!canGoStep2 || loadingDetails}
            onClick={() => void expandSelectedAndContinue()}
          >
            {loadingDetails ? 'Loading details…' : 'Next'}
          </Button>
        )}
        {step === 1 && (
          <Button variant="contained" disabled={!canImport} onClick={() => setStep(2)}>
            Next
          </Button>
        )}
        {step === 2 && !result && (
          <Button variant="contained" onClick={() => void handleImport()} disabled={submitting}>
            {submitting ? 'Importing…' : `Import ${selectedRows.length}`}
          </Button>
        )}
        {step === 2 && result && (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ImportPdkHoldersWizard;
